import React, { useMemo } from 'react';
import {
  Box, Text, Pressable, HStack, ScrollView, VStack, Icon,
} from '@gluestack-ui/themed';
import {
  Calendar, CalendarClock, FileCheck, BrainCircuit, PanelLeft, PanelLeftClose, Users,
} from 'lucide-react-native';
import dayjs from 'dayjs';
import { useListScheduledChanges } from '@/common/api/endpoints/scheduled-changes/scheduled-changes';
import { formatEnglishDayMonth } from '@/common/utils/formatEnglishDateTime';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { Module } from '@/common/api/models/module';

import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import SidebarItem from './components/SidebarItem';
import SectionHeader from './components/SectionHeader';
import { useLecturerSidebarData } from './hooks/useLecturerSidebarData';
import { LecturerRouteParamList } from './types';
import { getLatestVersion } from './utils/navigationUtils';
import { useLecturerLayoutStore } from './hooks/useLecturerLayoutStore';

interface LecturerSidebarProps {
  currentRoute?: string;
}

/**
 * Persistent navigation sidebar for the Lecturer role.
 */
const LecturerSidebar: React.FC<LecturerSidebarProps> = ({
  currentRoute,
}) => {
  const navigation = useNavigation<StackNavigationProp<LecturerRouteParamList>>();
  const { isSidebarCollapsed: isCollapsed, toggleSidebar } = useLecturerLayoutStore();
  const { isFeatureActive } = useUserFeatures();
  const isContentStudioActive = isFeatureActive('lecturer-content-studio');

  const {
    t,
    isModulesLoading,
    pendingCount,
    hasOverdueEvaluations,
    gapCount,
    hasCriticalGaps,
    hasEventTasks,
    authoredModules,
    evaluatorModules,
    isCalendarLoading,
  } = useLecturerSidebarData();

  const { data: scheduledListResponse, isLoading: isScheduledListLoading } = useListScheduledChanges();
  const scheduledChanges = useMemo(
    () => scheduledListResponse?.data?.data ?? [],
    [scheduledListResponse],
  );
  const showScheduledChanges = !isScheduledListLoading && scheduledChanges.length > 0;
  const scheduledBadgeText = useMemo(() => {
    const upcoming = scheduledChanges.filter((item) => item.state === 'scheduled');
    if (!upcoming.length) return undefined;
    const earliest = upcoming.reduce((best, item) => {
      if (!item.goLiveAt) return best;
      if (!best?.goLiveAt) return item;
      return new Date(item.goLiveAt).getTime() < new Date(best.goLiveAt).getTime() ? item : best;
    }, upcoming[0]);
    if (!earliest?.goLiveAt) return undefined;
    if (dayjs(earliest.goLiveAt).isSame(dayjs(), 'day')) {
      return t('lecturer.scheduledChanges.today');
    }
    return formatEnglishDayMonth(earliest.goLiveAt);
  }, [scheduledChanges, t]);

  // Navigate to module editor for the selected module
  const handleModulePress = (module: Module) => {
    const targetVersion = getLatestVersion(module);
    navigation.navigate('ModuleEditor', { moduleId: module.id, version: targetVersion, taskId: null });
  };

  const renderModuleRow = (module: Module) => (
    <Pressable
      key={module.id}
      onPress={() => handleModulePress(module)}
      p="$2.5"
      px="$3"
      my="$0.5"
      ml="$1.5"
      mr="$1.5"
      opacity={0.8}
    >
      <HStack alignItems="center" space="md">
        <Box w={4} h={16} bg="$textInverted" borderRadius="$full" />
        <Text
          size="sm"
          fontWeight="$medium"
          color="$textBody"
          flex={1}
          numberOfLines={1}
        >
          {module.title}
        </Text>
      </HStack>
    </Pressable>
  );

  return (
    <Box
      w={isCollapsed ? 64 : 280}
      bg="$white"
      borderRightWidth={1}
      borderRightColor="$borderLight"
      height="100%"
    >
      <ScrollView flex={1} pt="$2" showsVerticalScrollIndicator={false}>
        {isCollapsed ? (
          <VStack space="md" alignItems="center">
            <Pressable
              onPress={toggleSidebar}
              p="$2"
              mb="$2"
              borderRadius="$md"
              sx={{ ':hover': { bg: '$bgHover' } }}
            >
              <Icon as={PanelLeft} color="$textSubtle" size="sm" />
            </Pressable>
            <SidebarItem
              icon={FileCheck}
              label=""
              isActive={currentRoute === 'EvaluationDesk'}
              onPress={() => navigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false })}
              isCollapsed
            />
            <SidebarItem
              icon={Users}
              label=""
              isActive={currentRoute === 'Engagement'}
              onPress={() => navigation.navigate('Engagement')}
              isCollapsed
            />
            <SidebarItem
              icon={Calendar}
              label=""
              isActive={currentRoute === 'EventCalendar'}
              onPress={() => navigation.navigate('EventCalendar')}
              isCollapsed
              disabled={isCalendarLoading || !hasEventTasks}
            />
            {showScheduledChanges && (
              <SidebarItem
                icon={CalendarClock}
                label=""
                badgeText={scheduledBadgeText}
                isActive={currentRoute === 'ScheduledChanges'}
                onPress={() => navigation.navigate('ScheduledChanges', {})}
                isCollapsed
              />
            )}
            {isContentStudioActive && (
              <SidebarItem
                icon={BrainCircuit}
                label=""
                isActive={currentRoute === 'ContentStudio'}
                onPress={() => navigation.navigate('ContentStudio')}
                isCollapsed
              />
            )}
          </VStack>
        ) : (
          <>
            {/* Workspaces Section */}
            <SectionHeader
              title="Workspaces"
              rightAction={(
                <Pressable
                  onPress={toggleSidebar}
                  p="$1"
                  borderRadius="$md"
                  sx={{ ':hover': { bg: '$bgHover' } }}
                >
                  <Icon as={PanelLeftClose} color="$textSubtle" size="xs" />
                </Pressable>
              )}
            />

            <SidebarItem
              icon={FileCheck}
              label="Evaluations"
              badge={pendingCount}
              hasOverdue={hasOverdueEvaluations}
              isActive={currentRoute === 'EvaluationDesk'}
              onPress={() => navigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false })}
            />

            <SidebarItem
              icon={Users}
              label="Engagement"
              isActive={currentRoute === 'Engagement'}
              onPress={() => navigation.navigate('Engagement')}
            />

            <SidebarItem
              icon={Calendar}
              label="Calendar"
              badge={gapCount}
              hasOverdue={hasCriticalGaps}
              isActive={currentRoute === 'EventCalendar'}
              onPress={() => navigation.navigate('EventCalendar')}
              disabled={isCalendarLoading || !hasEventTasks}
            />

            {showScheduledChanges && (
              <SidebarItem
                icon={CalendarClock}
                label={t('lecturer.scheduledChanges.sidebarLabel')}
                badgeText={scheduledBadgeText}
                isActive={currentRoute === 'ScheduledChanges'}
                onPress={() => navigation.navigate('ScheduledChanges', {})}
              />
            )}

            {isContentStudioActive && (
              <SidebarItem
                icon={BrainCircuit}
                label="Content Studio"
                isActive={currentRoute === 'ContentStudio'}
                onPress={() => navigation.navigate('ContentStudio')}
              />
            )}

            {/* Learning Modules: split by authorship */}
            {isModulesLoading ? (
              <>
                <SectionHeader title={t('lecturer.sidebar.titles.myModules')} />
                <Box px="$4" py="$2">
                  <Text size="xs" color="$textHint">
                    Loading...
                  </Text>
                </Box>
              </>
            ) : (
              <>
                {authoredModules.length > 0 && (
                  <>
                    <SectionHeader title={t('lecturer.sidebar.titles.myModules')} />
                    {authoredModules.map(renderModuleRow)}
                  </>
                )}

                {evaluatorModules.length > 0 && (
                  <>
                    <SectionHeader title={t('lecturer.sidebar.titles.assignedForEvaluation')} />
                    {evaluatorModules.map(renderModuleRow)}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ScrollView>
    </Box>
  );
};

export default LecturerSidebar;
